import React, { useEffect, useRef, useState } from 'react';
import Prism from 'prismjs';
import 'prismjs/themes/prism.css'; // Switch to light theme
import 'prismjs/components/prism-markup'; // For HTML syntax
import 'prismjs/components/prism-css'; // For CSS syntax
import 'prismjs/components/prism-javascript'; // For JS syntax
import 'prismjs/plugins/line-numbers/prism-line-numbers.js'; // For line numbers
import 'prismjs/plugins/line-numbers/prism-line-numbers.css'; // Line number styles

// Function to format HTML with indentation and prepare for collapsible sections
const formatHTML = (html) => {
  if (!html) return '';
  
  const spacesPerIndent = 2; // Fixed indentation
  let formatted = '';
  let indent = 0;
  
  // Split by < to get tags and content
  const parts = html.split('<');
  
  // Store structure for collapsible sections
  const sections = [];
  const openTags = [];
  
  let lineIndex = 0;
  
  for (let i = 0; i < parts.length; i++) {
    if (!parts[i]) continue;
    
    const part = '<' + parts[i];
    
    // Check if this is a closing tag
    if (part.match(/<\//)) {
      indent--;
      
      // Check if we're closing a section
      const tagName = part.match(/<\/([a-zA-Z0-9_-]+)/)?.[1];
      if (tagName && openTags.length > 0 && openTags[openTags.length - 1].tagName === tagName) {
        const openTag = openTags.pop();
        // Add a section if it contains multiple lines
        if (lineIndex - openTag.lineIndex > 1) {
          sections.push({
            start: openTag.lineIndex,
            end: lineIndex,
            indent: openTag.indent
          });
        }
      }
    }
    
    // Add indentation
    const indentation = ' '.repeat(Math.max(0, indent * spacesPerIndent));
    
    // Add formatted line
    if (part.trim()) {
      formatted += indentation + part.trim() + '\n';
      lineIndex++;
      
      // Check if this is an opening tag that might have nested content
      const openMatch = part.match(/<([a-zA-Z0-9_-]+)/);
      if (openMatch && !part.match(/<(img|br|hr|input|link|meta)\b[^>]*>/i) && !part.match(/<[^>]+\/>/)) {
        const tagName = openMatch[1];
        openTags.push({
          tagName: tagName,
          lineIndex: lineIndex - 1,
          indent: indent
        });
      }
    }
    
    // Check if this is an opening tag that needs indentation for its content
    if (part.match(/<[^/][^>]*>/) && !part.match(/<(img|br|hr|input|link|meta)\b[^>]*>/i) && !part.match(/<[^>]+\/>/)) {
      indent++;
    }
  }
  
  return {
    html: formatted,
    sections: sections
  };
};

function CodePreview({ html }) {
  const codeRef = useRef(null);
  const [formattedHTML, setFormattedHTML] = useState('');
  const [collapsibleSections, setCollapsibleSections] = useState([]);
  const [collapsedSections, setCollapsedSections] = useState(new Set());
  const [copied, setCopied] = useState(false);
  
  // Format HTML when component loads or when html changes
  useEffect(() => {
    if (html) {
      const result = formatHTML(html);
      setFormattedHTML(result.html);
      setCollapsibleSections(result.sections);
    } else {
      setFormattedHTML('');
      setCollapsibleSections([]);
    }
  }, [html]);
  
  // Apply syntax highlighting when formattedHTML changes
  useEffect(() => {
    if (codeRef.current) {
      Prism.highlightElement(codeRef.current);
    }
  }, [formattedHTML]);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(html || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
  };
  
  const toggleSection = (index) => {
    const newCollapsedSections = new Set(collapsedSections);
    if (newCollapsedSections.has(index)) {
      newCollapsedSections.delete(index);
    } else {
      newCollapsedSections.add(index);
    }
    setCollapsedSections(newCollapsedSections);
  };
  
  // Add line numbers manually for better control
  const lines = formattedHTML.split('\n');
  const lineCount = lines.length;
  
  // Process lines for display, handling collapsed sections
  const visibleLines = [];
  const lineNumbers = [];
  
  for (let i = 0; i < lines.length; i++) {
    // Check if this line is part of a collapsed section
    const isCollapsed = collapsibleSections.some((section, index) => 
      collapsedSections.has(index) && i > section.start && i < section.end
    );
    
    if (!isCollapsed) {
      visibleLines.push(lines[i]);
      lineNumbers.push(i + 1);
    }
    
    // Add fold indicator after the opening tag of a collapsible section
    const sectionIndex = collapsibleSections.findIndex(section => section.start === i);
    if (sectionIndex !== -1) {
      const section = collapsibleSections[sectionIndex];
      const isCollapsed = collapsedSections.has(sectionIndex);
      
      if (isCollapsed) {
        // Add folded indicator
        const indent = ' '.repeat(section.indent * 2); // Fixed 2 spaces
        visibleLines.push(`${indent}// ... ${section.end - section.start - 1} folded lines ...`);
        lineNumbers.push('•••');
      }
    }
  }
  
  return (
    <div className="w-full h-full bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 overflow-auto border border-gray-200 dark:border-gray-700 rounded-md shadow-sm">
      <div className="p-4 flex items-center justify-between border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="font-medium text-gray-700 dark:text-gray-300">HTML Code</div>
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">{lineCount} lines</span>
          
          <button 
            onClick={handleCopy}
            className={`px-3 py-1 text-xs text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded border border-gray-300 dark:border-gray-600 transition-colors ${
              copied ? 'bg-green-50 dark:bg-green-900 text-green-600 dark:text-green-400 border-green-300 dark:border-green-700' : ''
            }`}
          >
            {copied ? 'Copied!' : 'Copy Code'}
          </button>
        </div>
      </div>
      <div className="flex">
        {/* Line numbers */}
        <div className="p-6 pt-[24px] text-right text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 select-none min-w-[50px]">
          {lineNumbers.map((num, i) => (
            <div key={i} className="leading-6 font-mono text-sm">
              {num}
            </div>
          ))}
        </div>
        {/* Code with syntax highlighting */}
        <div className="p-6 pt-[24px] w-full overflow-x-auto dark:bg-gray-900">
          {/* Add toggle buttons for collapsible sections */}
          {collapsibleSections.map((section, index) => {
            const isCollapsed = collapsedSections.has(index);
            const leftPos = section.indent * 16; // Approximate character width in pixels
            
            return (
              <div 
                key={index}
                className="absolute flex items-center z-10 cursor-pointer opacity-50 hover:opacity-100"
                style={{ 
                  top: `calc(24px + ${section.start * 24}px)`, 
                  left: `calc(6rem + ${leftPos}px - 20px)`,
                  height: '24px'
                }}
                onClick={() => toggleSection(index)}
                title={isCollapsed ? "Expand section" : "Collapse section"}
              >
                <div className="w-4 h-4 flex items-center justify-center bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600">
                  {isCollapsed ? '+' : '-'}
                </div>
              </div>
            );
          })}
          
          <pre className="language-markup h-full w-full relative">
            <code ref={codeRef} className="language-markup font-mono text-sm">
              {visibleLines.join('\n') || '<!-- No code generated yet. -->'}
            </code>
          </pre>
        </div>
      </div>
    </div>
  );
}

export { CodePreview }; 